export class Sounds {
  private ctx: AudioContext | null = null;
  private buffers: Map<string, AudioBuffer> = new Map();
  private current: number = 0;
  private loading: boolean = false;
  private resuming: Promise<void> | null = null;

  private readonly soundPaths: Record<string, string> = {
    c1: '/sound/c1.mp3',
    c2: '/sound/c2.mp3',
    c3: '/sound/c3.mp3',
    c4: '/sound/c4.mp3',
    fail: '/sound/cancel.mp3',
    correct: '/sound/correct.mp3',
    bonus: '/sound/bonus.mp3',
    win: '/sound/win.mp3',
  };

  constructor() {
    // Unlock audio on user interaction (required for iOS Safari)
    // Keep listeners permanently — iOS re-suspends on tab switch
    const unlock = () => {
      this.ensureContext();
    };
    document.addEventListener('touchstart', unlock, true);
    document.addEventListener('touchend', unlock, true);
    document.addEventListener('mousedown', unlock, true);
    document.addEventListener('click', unlock, true);

    // Listen for page visibility changes to resume context when returning
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.ctx) {
        this.ensureContext();
      }
    });
  }

  private ensureContext() {
    try {
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;

      if (!this.ctx) {
        this.ctx = new AC();

        // Play a silent buffer to fully unlock on iOS
        const silent = this.ctx.createBuffer(1, 1, 22050);
        const source = this.ctx.createBufferSource();
        source.buffer = silent;
        source.connect(this.ctx.destination);
        source.start(0);
      }

      // Always try to resume if suspended (iOS suspends on tab switch/app switch)
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {});
      }

      // Preload sounds if not already done
      if (!this.loading) {
        this.loading = true;
        this.preload();
      }
    } catch {
      // AudioContext not supported
    }
  }

  private async preload() {
    if (!this.ctx) return;

    const entries = Object.entries(this.soundPaths);
    await Promise.all(
      entries.map(async ([name, path]) => {
        try {
          const response = await fetch(path);
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await this.ctx!.decodeAudioData(arrayBuffer);
          this.buffers.set(name, audioBuffer);
        } catch {
          // Failed to load sound — silently skip
        }
      }),
    );
  }

  private play(name: string) {
    if (!this.ctx) return;

    const buffer = this.buffers.get(name);
    if (!buffer) return;

    // If context is suspended, resume it first but DON'T queue the sound
    // This prevents the "queued sounds burst" bug on iOS
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
      // Drop this sound — it would queue and play late
      return;
    }

    // Only play if context is running (not suspended, not closed)
    if (this.ctx.state !== 'running') return;

    try {
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.ctx.destination);
      source.start(0);
    } catch {
      // Playback failed
    }
  }

  public Click() {
    const names = ['c1', 'c2', 'c3', 'c4'];
    this.play(names[this.current]);
    this.current = (this.current + 1) % 4;
  }

  public soundFail() {
    this.play('fail');
  }

  public soundCorrect() {
    this.play('correct');
  }

  /** Bright chime for bonus word found */
  public soundBonus() {
    this.play('bonus');
  }

  /** Triumphant fanfare for game win */
  public soundWin() {
    this.play('win');
  }

  public PlayMusic() {
    return;
  }
}
